import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import "modules/DealerReports/components/DealerQualityChecksTabular/DealerQualityChecksTabular.scss";
import { questionStatusLookup } from "modules/DealerReports/components/DealerQualityChecksTabular/hooks";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { QualityCheckReportData } from "modules/DealerReports/components/DealerQualityChecksTabular";

type Props = {
  reportData: QualityCheckReportData[];
  isFetching?: boolean;
};

const columnHelper = createColumnHelper<QualityCheckReportData>();

export const DealerQualityChecksTable = ({ reportData, isFetching }: Props) => {
  const { t } = useTranslation();

  const columns = [
    columnHelper.accessor(row => row, {
      id: "location",
      header: t("v8_location", "Location"),
      cell: ({
        row: {
          original: { location }
        }
      }) => <span>{location}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "wo_date",
      header: t("v8_wo_data", "WO Data"),
      cell: ({
        row: {
          original: { wo_date }
        }
      }) => <span>{wo_date ? formatDate(wo_date, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "wo#",
      header: t("v8_wo", "WO #"),
      cell: ({
        row: {
          original: { appointment_id, wo_nr }
        }
      }) => (
        <Link to={`${PATHS.APPOINTMENTS}/${appointment_id}`} target="_blank" className="ellipsis wo-nr">
          <Icon className="arrow up right from square" />
          {wo_nr}
        </Link>
      ),
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "check_data",
      header: t("v8_check_date", "Check Date"),
      cell: ({
        row: {
          original: { check_date }
        }
      }) => <span>{check_date ? formatDate(check_date, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "mechanic",
      header: t("v8_mechanic", "Mechanic"),
      cell: ({
        row: {
          original: { mechanic }
        }
      }) => <span> {mechanic}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "qc_status",
      header: t("v8_qc_status", "QC Status"),
      cell: ({
        row: {
          original: { qc_status }
        }
      }) => {
        const qcStatus = questionStatusLookup.find(({ key }) => key === qc_status);
        return <span> {qcStatus ? t(qcStatus.translationKey) || qcStatus.defaultMessage : ""}</span>;
      },
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "qc_officer",
      header: t("v8_qc_officer", "QC Officer"),
      cell: ({
        row: {
          original: { qc_officer }
        }
      }) => <span> {qc_officer}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "qc_checklist",
      header: t("v8_qc_checklist", "QC Checklist"),
      cell: ({
        row: {
          original: { qc_checklist }
        }
      }) => <span> {qc_checklist}</span>,
      size: 150
    }),
    columnHelper.accessor(row => row, {
      id: "ok_with_note_remark",
      header: t("v8_ok_with_note_remark", "OK With Note"),
      cell: ({
        row: {
          original: { ok_with_note_remark }
        }
      }) => <span> {ok_with_note_remark}</span>,
      size: 150
    }),
    columnHelper.accessor(row => row, {
      id: "advised_remark",
      header: t("v8_advised_remark", "Advised Remarks"),
      cell: ({
        row: {
          original: { advised_remark }
        }
      }) => <span> {advised_remark}</span>,
      size: 150
    }),
    columnHelper.accessor(row => row, {
      id: "critical_remark",
      header: t("v8_critical_remark", "Critical Remarks"),
      cell: ({
        row: {
          original: { critical_remark }
        }
      }) => <span> {critical_remark}</span>,
      size: 150
    })
  ];

  return (
    <div className="table-container">
      <ReactTable columns={columns} data={reportData} isFetching={isFetching} />
    </div>
  );
};
